import React, { useState, useEffect } from 'react';
import { StageProps } from '../WizardContainer';
import { profileService } from '../../../services/api';
import { InterestCategory } from '../../../types/profile';

interface Stage4Props extends StageProps {
  step: number;
}

const Stage4Interests: React.FC<Stage4Props> = ({
  onNext,
  onBack,
  isLoading,
  step,
}) => {
  const [categories, setCategories] = useState<InterestCategory[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<number[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoadingCategories(true);
      try {
        const [cats, userInterests] = await Promise.all([
          profileService.getInterestCategories(),
          profileService.getMyInterests(),
        ]);
        setCategories(cats);
        setSelectedInterests(userInterests.map((i) => i.interestId));
      } finally {
        setLoadingCategories(false);
      }
    };
    loadData();
  }, []);

  const toggleInterest = (interestId: number) => {
    setSelectedInterests((prev) =>
      prev.includes(interestId)
        ? prev.filter((id) => id !== interestId)
        : [...prev, interestId]
    );
  };

  const handleNext = async () => {
    await profileService.updateInterests(selectedInterests);
    onNext({ interests: selectedInterests });
  };

  const getCategoriesForStep = (): InterestCategory[] => {
    // Split categories across 2 steps
    const halfLength = Math.ceil(categories.length / 2);
    if (step === 1) {
      return categories.slice(0, halfLength);
    }
    return categories.slice(halfLength);
  };

  if (loadingCategories) {
    return (
      <div className="wizard-stage">
        <div className="wizard-loading">Loading interests...</div>
      </div>
    );
  }

  const displayCategories = getCategoriesForStep();

  return (
    <div className="wizard-stage">
      <div className="wizard-step">
        <h3 className="wizard-step-title">
          {step === 1 ? 'Select your interests' : 'More interests'}
        </h3>
        <p className="wizard-step-subtitle">
          Choose what you're passionate about ({selectedInterests.length} selected)
        </p>

        <div className="wizard-interests-container">
          {displayCategories.map((category) => (
            <div key={category.id} className="wizard-interest-category">
              <button
                type="button"
                className={`wizard-interest-category-header ${
                  expandedCategory === category.id ? 'expanded' : ''
                }`}
                onClick={() =>
                  setExpandedCategory(
                    expandedCategory === category.id ? null : category.id
                  )
                }
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-name">{category.name}</span>
                <span className="category-count">
                  {
                    category.interests?.filter((i) =>
                      selectedInterests.includes(i.id)
                    ).length || 0
                  }
                  /{category.interests?.length || 0}
                </span>
                <span className="category-chevron">
                  {expandedCategory === category.id ? '▼' : '▶'}
                </span>
              </button>

              {expandedCategory === category.id && (
                <div className="wizard-interest-list">
                  {category.interests?.map((interest) => (
                    <button
                      key={interest.id}
                      type="button"
                      className={`wizard-interest-item ${
                        selectedInterests.includes(interest.id) ? 'selected' : ''
                      }`}
                      onClick={() => toggleInterest(interest.id)}
                    >
                      {interest.name}
                      {selectedInterests.includes(interest.id) && (
                        <span className="interest-check">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {selectedInterests.length < 3 && (
          <p className="wizard-warning">
            We recommend selecting at least 3 interests for better matches
          </p>
        )}
      </div>

      <div className="wizard-stage-actions">
        <button
          type="button"
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : step === 2 ? 'Complete Stage' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Stage4Interests;
